import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { ConfigProvider, Layout, Typography, Card, Row, Col, theme as antTheme } from 'antd';
import {
  RocketOutlined,
  GlobalOutlined,
  SafetyOutlined,
  CustomerServiceOutlined,
  LoginOutlined
} from '@ant-design/icons';
import trTR from 'antd/locale/tr_TR';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import LoginPage from './pages/LoginPage';
import ChatWidget from './components/ChatWidget';
import WidgetThemeProvider from './components/ChatWidget/WidgetThemeProvider';
import EmbedPage from './pages/EmbedPage';
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboardPage from './components/Admin/AdminDashboardPage';
import ChatOptionsPage from './components/Admin/ChatOptionsPage';
import PromptsPage from './components/Admin/PromptsPage';
import KnowledgeBasePage from './components/Admin/KnowledgeBasePage';
import UnansweredQuestionsPage from './components/Admin/UnansweredQuestionsPage';
import SettingsPage from './components/Admin/SettingsPage';
import AgentsPage from './components/Admin/AgentsPage';
import FeedbackPage from './components/Admin/FeedbackPage';
import ConsentRecordsPage from './components/Admin/ConsentRecordsPage';
import LeadsPage from './components/Admin/LeadsPage';
import './App.css';

const { Header, Content, Footer } = Layout;
const { Title, Paragraph } = Typography;

// Korunmus sayfa (giris yapmamis kullaniciyi login'e yonlendirir, opsiyonel rol kontrolu)
const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, agent } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (roles && !roles.includes(agent?.role)) return <Navigate to="/" />;
  return children;
};

// /admin girisi: CALL_CENTER dashboard'a erisemez, dogrudan Aranacaklar'a gider
const AdminIndexRedirect = () => {
  const { agent } = useAuth();
  if (agent?.role === 'CALL_CENTER') return <Navigate to="/admin/leads" replace />;
  return <AdminDashboardPage />;
};

// Demo ana sayfa icerigi
const DemoHomePage = () => {
  const features = [
    {
      icon: <GlobalOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
      title: 'Dünya Genelinde Hizmet',
      description: 'Dünyanın her yerinde otel ve uçuş rezervasyonu yapın.',
    },
    {
      icon: <SafetyOutlined style={{ fontSize: 32, color: '#52c41a' }} />,
      title: 'Guvenli Odeme',
      description: '256-bit SSL şifreleme ile güvenli ödeme yapın.',
    },
    {
      icon: <CustomerServiceOutlined style={{ fontSize: 32, color: '#722ed1' }} />,
      title: '7/24 Destek',
      description: 'Seyahat asistanımız her zaman yanınızda.',
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        className="demo-header"
        style={{
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <RocketOutlined style={{ fontSize: 28, color: '#1890ff', marginRight: 12 }} />
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            TravelCo
          </Title>
        </div>
        <Link to="/login" style={{ color: '#bbb', fontSize: 13 }}>
          <LoginOutlined style={{ marginRight: 4 }} />
          Personel Girisi
        </Link>
      </Header>

      <Content className="demo-content" style={{ background: '#f5f5f5' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Hero Section */}
          <Card
            style={{
              marginBottom: 32,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              border: 'none',
            }}
          >
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Title style={{ color: '#fff', marginBottom: 16 }}>
                Hayalinizdeki Seyahate Baslayin
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16 }}>
                En uygun fiyatli ucuslar ve oteller icin hemen arama yapin.
                <br />
                Sağ alttaki asistan butonuna tıklayarak yardım alabilirsiniz.
              </Paragraph>
            </div>
          </Card>

          {/* Features */}
          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} md={8} key={index}>
                <Card
                  hoverable
                  style={{
                    textAlign: 'center',
                    borderRadius: 12,
                    height: '100%',
                  }}
                >
                  <div style={{ marginBottom: 16 }}>{feature.icon}</div>
                  <Title level={5}>{feature.title}</Title>
                  <Paragraph type="secondary">{feature.description}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </Content>

      <Footer style={{ textAlign: 'center', background: '#fff' }}>
        TravelCo &copy;{new Date().getFullYear()} - Tüm Hakları Saklıdır
      </Footer>
    </Layout>
  );
};

// Admin theme wrapper — switches Ant Design algorithm based on theme context
const AdminThemeWrapper = ({ children }) => {
  const { theme: adminTheme } = useTheme();
  return (
    <ConfigProvider
      locale={trTR}
      theme={{
        algorithm: adminTheme === 'dark' ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#3b82f6',
          borderRadius: 8,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

const App = () => {
  return (
    <ConfigProvider
      locale={trTR}
      theme={{
        token: {
          colorPrimary: '#3b82f6',
          borderRadius: 8,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
      }}
    >
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Musteri tarafi - Ana sayfa + Chat Widget */}
            <Route
              path="/"
              element={
                <>
                  <DemoHomePage />
                  <WidgetThemeProvider>
                    <ChatWidget />
                  </WidgetThemeProvider>
                </>
              }
            />

            {/* Embed - baska sitelere iframe ile gomulen ciplak widget */}
            <Route path="/embed" element={<EmbedPage />} />

            {/* Login */}
            <Route path="/login" element={<LoginPage />} />

            {/* Admin tarafi */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={['ADMIN', 'CONTENT_ADMIN', 'CALL_CENTER']}>
                  <ThemeProvider>
                    <AdminThemeWrapper>
                      <AdminLayout />
                    </AdminThemeWrapper>
                  </ThemeProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminIndexRedirect />} />
              <Route path="leads" element={
                <ProtectedRoute roles={['ADMIN', 'CALL_CENTER']}><LeadsPage /></ProtectedRoute>
              } />
              <Route path="chat-options" element={
                <ProtectedRoute roles={['ADMIN']}><ChatOptionsPage /></ProtectedRoute>
              } />
              <Route path="prompts" element={
                <ProtectedRoute roles={['ADMIN']}><PromptsPage /></ProtectedRoute>
              } />
              <Route path="knowledge-base" element={
                <ProtectedRoute roles={['ADMIN', 'CONTENT_ADMIN']}><KnowledgeBasePage /></ProtectedRoute>
              } />
              <Route path="unanswered-questions" element={
                <ProtectedRoute roles={['ADMIN', 'CONTENT_ADMIN']}><UnansweredQuestionsPage /></ProtectedRoute>
              } />
              <Route path="feedback" element={
                <ProtectedRoute roles={['ADMIN']}><FeedbackPage /></ProtectedRoute>
              } />
              <Route path="agents" element={
                <ProtectedRoute roles={['ADMIN']}><AgentsPage /></ProtectedRoute>
              } />
              <Route path="settings" element={
                <ProtectedRoute roles={['ADMIN']}><SettingsPage /></ProtectedRoute>
              } />
              <Route path="consent-records" element={
                <ProtectedRoute roles={['ADMIN']}><ConsentRecordsPage /></ProtectedRoute>
              } />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
